import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchTsrFoodCategories,
  fetchTsrFoodItemsAdmin,
  saveTsrFoodItem
} from '../../api/tsrFoodApi';
import TsrAdminDataTable from '../../components/TsrAdminDataTable';
import {
  formatAmount,
  formatQty,
  SERVING_UNITS,
  toBooleanFromBit
} from '../../utils/orderFoodUtils';

const emptyForm = {
  FoodItemId: null,
  FoodCategoryId: '',
  ItemName: '',
  Description: '',
  Rate: '',
  DefaultQtyPerPerson: '1',
  DefaultQtyPerKid: '',
  ServingUnit: 'pcs',
  ScaleByPersons: true,
  DisplayOrder: '0',
  GlobalOrder: '0',
  IsActive: true
};

function formatCategoryLabel(category) {
  const subCategory = category.SubCategoryName ? ` - ${category.SubCategoryName}` : '';
  return `${category.CategoryName}${subCategory}`;
}

function TsrAdminMenuItemsPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [itemsResponse, categoriesResponse] = await Promise.all([
        fetchTsrFoodItemsAdmin(),
        fetchTsrFoodCategories()
      ]);
      setItems(Array.isArray(itemsResponse?.data) ? itemsResponse.data : []);
      setCategories(Array.isArray(categoriesResponse?.data) ? categoriesResponse.data : []);
    } catch (err) {
      setError(err.message || 'Unable to load menu items.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const categoryOptions = useMemo(() => {
    if (!categories.length) {
      return ['All'];
    }
    return ['All', ...new Set(categories.map((category) => category.CategoryName))];
  }, [categories]);

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    return items.filter((item) => {
      const matchesCategory = filterCategory === 'All' || item.CategoryName === filterCategory;
      if (!matchesCategory) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      const haystack = [
        item.ItemName,
        item.CategoryName,
        item.SubCategoryName,
        item.Description
      ].join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [filterCategory, items, searchText]);

  const openCreateForm = () => {
    setForm({
      ...emptyForm,
      FoodCategoryId: categories[0]?.FoodCategoryId ? String(categories[0].FoodCategoryId) : ''
    });
    setShowForm(true);
    setError('');
  };

  const openEditForm = useCallback((item) => {
    setForm({
      FoodItemId: item.FoodItemId,
      FoodCategoryId: String(item.FoodCategoryId || ''),
      ItemName: item.ItemName || '',
      Description: item.Description || '',
      Rate: item.Rate == null ? '' : String(item.Rate),
      DefaultQtyPerPerson: item.DefaultQtyPerPerson == null ? '' : String(item.DefaultQtyPerPerson),
      DefaultQtyPerKid: item.DefaultQtyPerKid == null ? '' : String(item.DefaultQtyPerKid),
      ServingUnit: item.ServingUnit || 'pcs',
      ScaleByPersons: toBooleanFromBit(item.ScaleByPersons, true),
      DisplayOrder: String(item.DisplayOrder ?? 0),
      GlobalOrder: String(item.GlobalOrder ?? 0),
      IsActive: toBooleanFromBit(item.IsActive, true)
    });
    setShowForm(true);
    setError('');
  }, []);

  const columns = useMemo(() => [
    {
      name: 'Item',
      sortable: true,
      selector: (row) => row.ItemName || '',
      grow: 2,
      cell: (row) => (
        <div>
          <div className="fw-semibold">{row.ItemName}</div>
          {row.Description ? <small className="text-muted">{row.Description}</small> : null}
        </div>
      )
    },
    {
      name: 'Category',
      sortable: true,
      selector: (row) => `${row.CategoryName || ''} ${row.SubCategoryName || ''}`.trim(),
      grow: 1.5,
      cell: (row) => (
        <small>
          {row.CategoryName}
          {row.SubCategoryName ? ` - ${row.SubCategoryName}` : ''}
        </small>
      )
    },
    {
      name: 'Rate',
      sortable: true,
      selector: (row) => Number(row.Rate) || 0,
      cell: (row) => formatAmount(row.Rate || 0)
    },
    {
      name: 'Qty/adult',
      sortable: true,
      selector: (row) => Number(row.DefaultQtyPerPerson) || 0,
      cell: (row) => (row.DefaultQtyPerPerson == null ? '—' : formatQty(row.DefaultQtyPerPerson))
    },
    {
      name: 'Qty/kid',
      sortable: true,
      selector: (row) => Number(row.DefaultQtyPerKid) || 0,
      cell: (row) => (row.DefaultQtyPerKid == null ? '—' : formatQty(row.DefaultQtyPerKid))
    },
    {
      name: 'Unit',
      sortable: true,
      selector: (row) => row.ServingUnit || 'pcs'
    },
    {
      name: 'Scale',
      sortable: true,
      selector: (row) => (toBooleanFromBit(row.ScaleByPersons, true) ? 1 : 0),
      cell: (row) => (toBooleanFromBit(row.ScaleByPersons, true) ? 'Yes' : 'No')
    },
    {
      name: 'Order',
      sortable: true,
      selector: (row) => Number(row.GlobalOrder) || 0,
      cell: (row) => <small>{row.GlobalOrder}/{row.DisplayOrder}</small>
    },
    {
      name: 'Status',
      sortable: true,
      selector: (row) => (toBooleanFromBit(row.IsActive, false) ? 1 : 0),
      cell: (row) => (
        <span className={`badge ${toBooleanFromBit(row.IsActive, false) ? 'bg-success' : 'bg-secondary'}`}>
          {toBooleanFromBit(row.IsActive, false) ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      name: 'Actions',
      cell: (row) => (
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openEditForm(row)}>
          Edit
        </button>
      ),
      ignoreRowClick: true,
      width: '100px'
    }
  ], [openEditForm]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!form.FoodCategoryId) return 'Category is required.';
    if (!form.ItemName.trim()) return 'Item name is required.';
    if (form.Rate !== '' && Number(form.Rate) < 0) return 'Rate cannot be negative.';
    return '';
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError('');
      await saveTsrFoodItem({
        FoodItemId: form.FoodItemId,
        FoodCategoryId: Number(form.FoodCategoryId),
        ItemName: form.ItemName.trim(),
        Description: form.Description.trim(),
        Rate: form.Rate === '' ? 0 : Number(form.Rate),
        DefaultQtyPerPerson: form.DefaultQtyPerPerson === '' ? null : Number(form.DefaultQtyPerPerson),
        DefaultQtyPerKid: form.DefaultQtyPerKid === '' ? null : Number(form.DefaultQtyPerKid),
        ServingUnit: form.ServingUnit || 'pcs',
        ScaleByPersons: form.ScaleByPersons ? 1 : 0,
        DisplayOrder: Number(form.DisplayOrder) || 0,
        GlobalOrder: Number(form.GlobalOrder) || 0,
        IsActive: form.IsActive ? 1 : 0
      });
      setShowForm(false);
      setForm({ ...emptyForm });
      await loadData();
    } catch (err) {
      setError(err.message || 'Unable to save food item.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Menu Items</h4>
          <p className="text-muted mb-0">Add and edit food items used in package builder and customer ordering.</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={openCreateForm} disabled={!categories.length}>
          Add item
        </button>
      </div>

      {error ? <div className="alert alert-danger py-2">{error}</div> : null}

      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search items..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </div>
        <div className="col-md-4">
          <select
            className="form-select"
            value={filterCategory}
            onChange={(event) => setFilterCategory(event.target.value)}
          >
            {categoryOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      <TsrAdminDataTable
        columns={columns}
        data={filteredItems}
        loading={loading}
        defaultSortFieldId={1}
        noDataComponent="No menu items found."
      />

      {showForm ? (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{form.FoodItemId ? 'Edit menu item' : 'Add menu item'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowForm(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Category</label>
                    <select
                      className="form-select"
                      value={form.FoodCategoryId}
                      onChange={(event) => updateField('FoodCategoryId', event.target.value)}
                    >
                      <option value="">Select category</option>
                      {categories.map((category) => (
                        <option key={category.FoodCategoryId} value={String(category.FoodCategoryId)}>
                          {formatCategoryLabel(category)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Item name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.ItemName}
                      onChange={(event) => updateField('ItemName', event.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={form.Description}
                      onChange={(event) => updateField('Description', event.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Rate</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control"
                      value={form.Rate}
                      onChange={(event) => updateField('Rate', event.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Default qty per adult</label>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      className="form-control"
                      value={form.DefaultQtyPerPerson}
                      onChange={(event) => updateField('DefaultQtyPerPerson', event.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Default qty per kid</label>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      className="form-control"
                      value={form.DefaultQtyPerKid}
                      onChange={(event) => updateField('DefaultQtyPerKid', event.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Serving unit</label>
                    <select
                      className="form-select"
                      value={form.ServingUnit}
                      onChange={(event) => updateField('ServingUnit', event.target.value)}
                    >
                      {SERVING_UNITS.map((unit) => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Display order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.DisplayOrder}
                      onChange={(event) => updateField('DisplayOrder', event.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Global order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.GlobalOrder}
                      onChange={(event) => updateField('GlobalOrder', event.target.value)}
                    />
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={form.ScaleByPersons}
                        onChange={(event) => updateField('ScaleByPersons', event.target.checked)}
                        id="item-scale-by-persons"
                      />
                      <label className="form-check-label" htmlFor="item-scale-by-persons">Scale by persons</label>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={form.IsActive}
                        onChange={(event) => updateField('IsActive', event.target.checked)}
                        id="item-active"
                      />
                      <label className="form-check-label" htmlFor="item-active">Active</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save item'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default TsrAdminMenuItemsPage;
